import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { DateTime } from 'luxon';

type ConversationPanelProps = {
  locationId?: string;
  contactId?: string;
  detail?: ZazuConversationDetail | null;
  isLoading: boolean;
};

const formatWhen = (iso?: string | null): string =>
  iso ? DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED) : '';

/**
 * The stored conversation for the contact currently being filtered on.
 *
 * It sits beside the audit trail rather than inside it because the two are
 * different shapes: the trail is one row per turn zazu handled, the transcript
 * is an ordered record of everything said — including messages a human
 * negotiator typed, which produce no audit row at all. Reading those side by
 * side is the point.
 *
 * Ordering follows `seq`, never the timestamp: a transcript replayed from GHL
 * carries no per-message time, so every message recovered in one request shares
 * a single instant.
 */
const ConversationPanel: React.FC<ConversationPanelProps> = ({
  locationId,
  contactId,
  detail,
  isLoading
}) => {
  // Both halves of the key are needed — a conversation is (location, contact).
  if (!locationId || !contactId) {
    return (
      <Box sx={{ mb: 3, px: 2, py: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <ForumOutlinedIcon fontSize="small" color="disabled" />
          <Typography variant="body2" color="text.secondary">
            Pick a location and enter a Core Contact ID to see the stored conversation.
          </Typography>
        </Stack>
      </Box>
    );
  }

  const messages = detail?.messages ?? [];
  const header = detail?.conversation;

  return (
    <Accordion defaultExpanded sx={{ mb: 3 }} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ForumOutlinedIcon fontSize="small" />
            <Typography variant="subtitle1">Conversation</Typography>
          </Stack>
          {header ? (
            <>
              <Chip size="small" label={`${header.messageCount} messages`} />
              {header.assistantName ? <Chip size="small" variant="outlined" label={header.assistantName} /> : null}
              {header.lastMessageAt ? (
                <Typography variant="caption" color="text.secondary">
                  last message {formatWhen(header.lastMessageAt)}
                </Typography>
              ) : null}
            </>
          ) : null}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        {isLoading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading conversation…
            </Typography>
          </Stack>
        ) : messages.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No stored conversation for this contact yet. zazu began recording transcripts from the
            conversation-store release, so older contacts have nothing here.
          </Typography>
        ) : (
          <Stack spacing={1.5} sx={{ maxHeight: 460, overflowY: 'auto', pr: 1 }}>
            {messages.map((message) => {
              const isAssistant = message.role === 'assistant';
              // A `transcript` row was recovered from GHL's history: for an
              // assistant turn that means it may be something a human
              // negotiator typed, which no audit trail row covers.
              const fromHuman = isAssistant && message.source !== 'generated';

              return (
                <Box
                  key={message.id}
                  sx={{
                    alignSelf: isAssistant ? 'flex-end' : 'flex-start',
                    maxWidth: '78%',
                    px: 1.75,
                    py: 1.25,
                    borderRadius: 2,
                    border: 1,
                    borderColor: fromHuman ? 'warning.light' : 'divider',
                    bgcolor: isAssistant ? 'action.hover' : 'background.paper'
                  }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      #{message.seq} · {isAssistant ? 'assistant' : 'contact'}
                    </Typography>
                    {fromHuman ? (
                      <Chip size="small" color="warning" variant="outlined" label="not written by zazu" />
                    ) : null}
                    {message.model ? (
                      <Typography variant="caption" color="text.secondary">
                        {message.model}
                      </Typography>
                    ) : null}
                    {message.responseId ? (
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {message.responseId}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ConversationPanel;
